import React, { useState, useEffect, useRef } from 'react';
import { consultasAPI, emailAPI, authAPI } from '../services/api';
import Editor from './Editor';

function ParticipantePicker({ clientes, selected, onChange }) {
  const [busqueda, setBusqueda] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    const idStr = id.toString();
    const next = selected.includes(idStr)
      ? selected.filter(s => s !== idStr)
      : [...selected, idStr];
    onChange(next);
  };

  const remove = (id) => onChange(selected.filter(s => s !== id.toString()));

  const filtrados = clientes.filter(c => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return `${c.nombre} ${c.apellidos}`.toLowerCase().includes(q);
  });

  const selectedClientes = clientes.filter(c => selected.includes(c.id.toString()));

  const pStyles = {
    wrapper: { position: 'relative' },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: selected.length > 0 ? 8 : 0 },
    chip: { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#e8e0ff', color: '#5b21b6', padding: '4px 10px', borderRadius: 16, fontSize: 13, fontWeight: 500 },
    chipX: { background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 8px 8px', maxHeight: 200, overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
    row: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 14, transition: 'background 0.15s' },
    check: { width: 16, height: 16, accentColor: '#7c3aed' },
    empty: { padding: '12px', color: '#999', fontSize: 13, textAlign: 'center' },
    count: { display: 'block', color: '#666', fontSize: 12, marginTop: 4 },
  };

  return (
    <div className="form-group">
      <label>Participantes *</label>
      {selectedClientes.length > 0 && (
        <div style={pStyles.chips}>
          {selectedClientes.map(c => (
            <span key={c.id} style={pStyles.chip}>
              {c.nombre} {c.apellidos}
              <button type="button" style={pStyles.chipX} onClick={() => remove(c.id)} title="Quitar">&times;</button>
            </span>
          ))}
        </div>
      )}
      <div ref={ref} style={pStyles.wrapper}>
        <input
          type="text"
          placeholder="Buscar clientes..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          onFocus={() => setOpen(true)}
          style={pStyles.input}
        />
        {open && (
          <div style={pStyles.dropdown}>
            {filtrados.length > 0 ? filtrados.map(c => {
              const isSelected = selected.includes(c.id.toString());
              return (
                <div
                  key={c.id}
                  style={{ ...pStyles.row, background: isSelected ? '#f5f0ff' : 'transparent' }}
                  onClick={() => toggle(c.id)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isSelected ? '#ede5ff' : '#f8f9fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? '#f5f0ff' : 'transparent'; }}
                >
                  <input type="checkbox" checked={isSelected} readOnly style={pStyles.check} />
                  <span>{c.nombre} {c.apellidos}</span>
                </div>
              );
            }) : (
              <div style={pStyles.empty}>No se encontraron clientes</div>
            )}
          </div>
        )}
      </div>
      <small style={pStyles.count}>
        {selected.length} participante{selected.length !== 1 ? 's' : ''} seleccionado{selected.length !== 1 ? 's' : ''}
      </small>
    </div>
  );
}

function CitaForm({ cita, clientes, onClose, onSubmit, onDelete, fechaInicial, openEmailModalOnMount = false, onOpenEmailModalShown, citasGrupalesHabilitadas = false, videollamadasHabilitadas = false }) {
  // Función para calcular la fecha/hora inicial
  const calcularFechaHoraInicial = () => {
    if (fechaInicial) {
      const fecha = fechaInicial instanceof Date ? fechaInicial : new Date(fechaInicial);
      const fechaHora = new Date(
        fecha.getFullYear(),
        fecha.getMonth(),
        fecha.getDate(),
        9, 0, 0
      );
      const year = fechaHora.getFullYear();
      const month = String(fechaHora.getMonth() + 1).padStart(2, '0');
      const day = String(fechaHora.getDate()).padStart(2, '0');
      const hours = String(fechaHora.getHours()).padStart(2, '0');
      const minutes = String(fechaHora.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else if (!cita) {
      const ahora = new Date();
      ahora.setHours(ahora.getHours() + 1);
      ahora.setMinutes(0);
      const year = ahora.getFullYear();
      const month = String(ahora.getMonth() + 1).padStart(2, '0');
      const day = String(ahora.getDate()).padStart(2, '0');
      const hours = String(ahora.getHours()).padStart(2, '0');
      const minutes = String(ahora.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    return '';
  };

  const [validationError, setValidationError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    authAPI.check().then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);
  const [formEmail, setFormEmail] = useState({ email: '', mensaje: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [formData, setFormData] = useState({
    cliente_id: '',
    titulo: '',
    numero_sesion: '',
    fecha_hora: calcularFechaHoraInicial(),
    duracion_minutos: 60,
    descripcion: '',
    estado: 'confirmada',
    modalidad: 'Presencial',
    recordatorio: true,
    enviarEmailAlCrear: false,
    es_grupal: false,
    participantes: [],
  });

  useEffect(() => {
    if (cita) {
      // Convertir fecha_hora a formato datetime-local sin cambiar zona horaria
      const fechaHora = new Date(cita.fecha_hora);
      // Usar métodos locales para evitar conversión UTC
      const year = fechaHora.getFullYear();
      const month = String(fechaHora.getMonth() + 1).padStart(2, '0');
      const day = String(fechaHora.getDate()).padStart(2, '0');
      const hours = String(fechaHora.getHours()).padStart(2, '0');
      const minutes = String(fechaHora.getMinutes()).padStart(2, '0');
      const fechaHoraStr = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      setFormData({
        cliente_id: cita.cliente_id.toString(),
        titulo: cita.titulo || '',
        numero_sesion: cita.numero_sesion || '',
        fecha_hora: fechaHoraStr,
        duracion_minutos: cita.duracion_minutos || 60,
        descripcion: cita.descripcion || '',
        estado: cita.estado || 'confirmada',
        modalidad: cita.modalidad || 'Presencial',
        recordatorio: cita.recordatorio !== undefined ? cita.recordatorio : true,
        enviarEmailAlCrear: false,
        es_grupal: !!cita.es_grupal,
        participantes: cita.participantes ? cita.participantes.map(p => p.cliente_id.toString()) : [],
      });
    } else if (fechaInicial) {
      // Si hay una fecha inicial (clic en calendario), usarla
      // Crear una nueva fecha usando los componentes de la fecha inicial
      // Esto evita problemas de zona horaria
      const fecha = fechaInicial instanceof Date ? fechaInicial : new Date(fechaInicial);
      const fechaHora = new Date(
        fecha.getFullYear(),
        fecha.getMonth(),
        fecha.getDate(),
        9, // Hora por defecto: 9:00 AM
        0, // Minutos: 0
        0  // Segundos: 0
      );
      
      const year = fechaHora.getFullYear();
      const month = String(fechaHora.getMonth() + 1).padStart(2, '0');
      const day = String(fechaHora.getDate()).padStart(2, '0');
      const hours = String(fechaHora.getHours()).padStart(2, '0');
      const minutes = String(fechaHora.getMinutes()).padStart(2, '0');
      const fechaHoraStr = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      setFormData(prev => ({
        ...prev,
        fecha_hora: fechaHoraStr,
      }));
    }
  }, [cita, fechaInicial]);

  // Efecto para auto-rellenar número de sesión y modalidad cuando se selecciona un cliente
  useEffect(() => {
    const calcularSiguienteNumeroSesion = async () => {
      // Solo si estamos creando una nueva cita (no editando) y hay un cliente seleccionado
      if (!cita && formData.cliente_id) {
        try {
          const response = await consultasAPI.getAll(parseInt(formData.cliente_id));
          const consultas = response.data || [];
          
          // Encontrar el máximo número de sesión
          let maxNumero = 0;
          consultas.forEach(consulta => {
            if (consulta.numero_sesion && consulta.numero_sesion > maxNumero) {
              maxNumero = consulta.numero_sesion;
            }
          });
          
          // Si no hay sesiones, usar 1, sino maxNumero + 1
          const siguienteNumero = maxNumero === 0 ? 1 : maxNumero + 1;
          
          // Obtener la modalidad preferida del cliente
          const clienteSeleccionado = clientes.find(c => c.id === parseInt(formData.cliente_id));
          const modalidadPreferida = clienteSeleccionado?.modalidad_preferida || 'Presencial';
          
          
          // Actualizar número de sesión y modalidad
          setFormData(prev => {
            const updates = {};
            if (!prev.numero_sesion || prev.numero_sesion === '') {
              updates.numero_sesion = siguienteNumero;
            }
            // Auto-rellenar modalidad con la preferida del cliente (siempre que no estemos editando una cita existente)
            // Actualizar siempre la modalidad cuando se selecciona un cliente nuevo
            if (!cita) {
              updates.modalidad = modalidadPreferida;
            }
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
          });
        } catch (err) {
          console.error('Error al obtener sesiones del cliente:', err);
          // Si hay error, usar 1 por defecto y modalidad del cliente si está disponible
          const clienteSeleccionado = clientes.find(c => c.id === parseInt(formData.cliente_id));
          const modalidadPreferida = clienteSeleccionado?.modalidad_preferida || 'Presencial';
          
          setFormData(prev => {
            const updates = {};
            if (!prev.numero_sesion || prev.numero_sesion === '') {
              updates.numero_sesion = 1;
            }
            // Auto-rellenar modalidad con la preferida del cliente (siempre que no estemos editando una cita existente)
            if (!cita) {
              updates.modalidad = modalidadPreferida;
            }
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
          });
        }
      }
    };

    calcularSiguienteNumeroSesion();
  }, [formData.cliente_id, cita, clientes]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value),
    }));
  };

  const handleAbrirEnviarEmail = () => {
    const cliente = clientes.find(c => c.id === parseInt(formData.cliente_id, 10));
    setFormEmail({
      email: cliente?.email || '',
      mensaje: '',
    });
    setEmailError(null);
    setEmailSuccess(null);
    setShowEmailModal(true);
  };

  const handleEnviarEmailSubmit = async () => {
    if (!formEmail.email || !formEmail.email.includes('@')) {
      setEmailError('Introduce un email válido.');
      return;
    }
    setEmailError(null);
    setEmailSending(true);
    try {
      const response = await emailAPI.send('cita', cita.id, formEmail.email, formEmail.mensaje);
      if (response.data?.success) {
        setEmailSuccess('Recordatorio enviado correctamente.');
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSuccess(null);
        }, 1500);
      } else {
        setEmailError(response.data?.error || 'Error al enviar el email.');
      }
    } catch (e) {
      setEmailError(e.response?.data?.error || 'Error al enviar el email.');
    } finally {
      setEmailSending(false);
    }
  };

  // Abrir modal de email al crear cita si el padre lo pide (openEmailModalOnMount)
  useEffect(() => {
    if (cita && openEmailModalOnMount && onOpenEmailModalShown) {
      const cliente = clientes.find(c => c.id === parseInt(cita.cliente_id, 10));
      setFormEmail({ email: cliente?.email || '', mensaje: '' });
      setEmailError(null);
      setEmailSuccess(null);
      setShowEmailModal(true);
      onOpenEmailModalShown();
    }
  }, [cita, openEmailModalOnMount, onOpenEmailModalShown, clientes]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError(null);
    const clienteId = formData.cliente_id ? parseInt(formData.cliente_id, 10) : null;
    const duracion = parseInt(formData.duracion_minutos, 10) || 0;
    if (!clienteId) {
      setValidationError('Selecciona un cliente.');
      return;
    }
    if (duracion <= 0 || duracion > 480) {
      setValidationError('La duración debe estar entre 1 y 480 minutos.');
      return;
    }
    const fechaHoraLocal = new Date(formData.fecha_hora);
    const year = fechaHoraLocal.getFullYear();
    const month = String(fechaHoraLocal.getMonth() + 1).padStart(2, '0');
    const day = String(fechaHoraLocal.getDate()).padStart(2, '0');
    const hours = String(fechaHoraLocal.getHours()).padStart(2, '0');
    const minutes = String(fechaHoraLocal.getMinutes()).padStart(2, '0');
    const seconds = String(fechaHoraLocal.getSeconds()).padStart(2, '0');
    const fechaHoraMySQL = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    const data = {
      ...formData,
      cliente_id: clienteId,
      fecha_hora: fechaHoraMySQL,
      enviarEmailAlCrear: !!formData.enviarEmailAlCrear,
      es_grupal: formData.es_grupal ? 1 : 0,
      participantes: formData.es_grupal ? formData.participantes.map(id => parseInt(id, 10)) : [],
    };
    onSubmit(data);
  };

  return (
    <div
      className="modal"
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', overflowY: 'auto', zIndex: 4000 }}
    >
      <div
        className="modal-content"
        style={{ position: 'relative', zIndex: 4001 }}
      >
        <div className="modal-header">
          <h2>{cita ? 'Editar Cita' : 'Nueva Cita'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        {validationError && (
          <div className="alert alert-error" style={{ margin: '0 20px 16px' }}>{validationError}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Cliente *</label>
            <select
              name="cliente_id"
              value={formData.cliente_id}
              onChange={handleChange}
              required
            >
              <option value="">Selecciona un cliente</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nombre} {cliente.apellidos}
                </option>
              ))}
            </select>
          </div>
          {citasGrupalesHabilitadas && (
            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.es_grupal}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      es_grupal: checked,
                      participantes: checked && prev.cliente_id ? [prev.cliente_id] : [],
                    }));
                  }}
                />
                Cita grupal (múltiples clientes)
              </label>
            </div>
          )}
          {formData.es_grupal && (
            <ParticipantePicker
              clientes={clientes}
              selected={formData.participantes}
              onChange={(selected) => {
                setFormData(prev => ({
                  ...prev,
                  participantes: selected,
                  cliente_id: selected.length > 0 ? selected[0] : prev.cliente_id,
                }));
              }}
            />
          )}
          <div className="form-group">
            <label>Título *</label>
            <input
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Número de Sesión</label>
            <input
              type="number"
              name="numero_sesion"
              value={formData.numero_sesion}
              onChange={handleChange}
              min="1"
            />
          </div>
          <div className="form-group">
            <label>Fecha y Hora *</label>
            <input
              type="datetime-local"
              name="fecha_hora"
              value={formData.fecha_hora}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Duración (minutos) *</label>
            <input
              type="number"
              name="duracion_minutos"
              value={formData.duracion_minutos}
              onChange={handleChange}
              min="15"
              step="15"
              required
            />
          </div>
          <div className="form-group">
            <label>Estado</label>
            <select
              name="estado"
              value={formData.estado}
              onChange={handleChange}
            >
              <option value="confirmada">Confirmada</option>
              <option value="cancelada">Cancelada</option>
              <option value="completada">Completada</option>
            </select>
          </div>
          <div className="form-group">
            <label>Modalidad</label>
            <select
              name="modalidad"
              value={formData.modalidad}
              onChange={handleChange}
            >
              <option value="Presencial">Presencial</option>
              <option value="Online">Online</option>
            </select>
          </div>
          {formData.modalidad === 'Online' && (
            videollamadasHabilitadas ? (
              <div style={{ padding: '10px 14px', background: '#f3e8ff', borderLeft: '4px solid #7c3aed', borderRadius: '4px', marginBottom: '16px', fontSize: '13px', color: '#5b21b6' }}>
                {cita && cita.sala_video_token ? (
                  (() => {
                    const hoyInicio = new Date(); hoyInicio.setHours(0,0,0,0);
                    const fechaCitaDia = new Date(cita.fecha_hora); fechaCitaDia.setHours(0,0,0,0);
                    const esPasada = fechaCitaDia < hoyInicio;
                    return esPasada ? (
                      <span style={{ color: '#adb5bd' }}>
                        📹 Videollamada no disponible (sesión pasada)
                      </span>
                    ) : (
                      <span>
                        📹 Enlace de videollamada:{' '}
                        <a href={`/videollamada/${cita.sala_video_token}`} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed', fontWeight: 600 }}>
                          Abrir sala
                        </a>
                        <button
                          type="button"
                          style={{ marginLeft: 8, background: 'none', border: '1px solid #7c3aed', borderRadius: 4, color: '#7c3aed', padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}
                          onClick={() => {
                            const url = `${window.location.origin}/videollamada/${cita.sala_video_token}`;
                            navigator.clipboard.writeText(url).then(() => alert('Enlace copiado'));
                          }}
                        >
                          Copiar enlace
                        </button>
                      </span>
                    );
                  })()
                ) : (
                  <span>📹 Se generará un enlace de videollamada automáticamente al guardar.</span>
                )}
              </div>
            ) : null
          )}
          <div className="form-group">
            <label>Comentarios</label>
            <Editor
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              height={200}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="recordatorio"
                checked={formData.recordatorio}
                onChange={handleChange}
              />
              Enviar recordatorio
            </label>
          </div>
          {!cita && (
            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="enviarEmailAlCrear"
                  checked={formData.enviarEmailAlCrear}
                  onChange={handleChange}
                />
                Enviar email con la información de la cita al cliente
              </label>
              <small style={{ display: 'block', color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Tras crear la cita se abrirá el envío de email al cliente con los detalles.
              </small>
            </div>
          )}
          {cita && (
            <div className="form-group" style={{ marginBottom: 16, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleAbrirEnviarEmail}
              >
                📧 Enviar recordatorio por email
              </button>
              {(currentUser?.rol === 'admin' || currentUser?.whatsapp_habilitado === true || currentUser?.whatsapp_habilitado === 1) && (() => {
                const cliente = clientes.find(c => c.id === parseInt(formData.cliente_id, 10));
                const phone = cliente?.whatsapp || cliente?.telefono || '';
                if (!phone) return null;
                return (
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ background: '#25D366', color: '#fff', border: 'none', padding: '8px 16px' }}
                    onClick={() => {
                      const cleanPhone = phone.replace(/[^0-9]/g, '');
                      const fh = new Date(formData.fecha_hora);
                      const fechaStr = fh.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      const horaStr = fh.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                      const nombre = cliente ? `${cliente.nombre || ''} ${cliente.apellidos || ''}`.trim() : '';
                      const texto = `Hola ${nombre}, te recuerdo tu cita "${formData.titulo}" el ${fechaStr} a las ${horaStr}. Modalidad: ${formData.modalidad || 'Presencial'}. Duración: ${formData.duracion_minutos} min.`;
                      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(texto)}`, '_blank');
                    }}
                    title="Enviar recordatorio por WhatsApp"
                  >
                    WhatsApp
                  </button>
                );
              })()}
            </div>
          )}
          <div className="form-actions">
            {cita && onDelete && (
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={onDelete}
                style={{ marginRight: 'auto' }}
              >
                Eliminar
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {cita ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>

        {showEmailModal && (
          <div className="cita-email-modal-overlay" onClick={() => !emailSending && setShowEmailModal(false)}>
            <div className="cita-email-modal-box" onClick={e => e.stopPropagation()}>
              <h3>Enviar recordatorio por email</h3>
              {(() => {
                const cliente = clientes.find(c => c.id === parseInt(formData.cliente_id, 10));
                return cliente ? <p className="cita-email-modal-cliente">Cliente: <strong>{cliente.nombre} {cliente.apellidos}</strong></p> : null;
              })()}
              {emailError && <div className="alert alert-error">{emailError}</div>}
              {emailSuccess && <div className="alert alert-success">{emailSuccess}</div>}
              <div className="form-group">
                <label>Email del destinatario *</label>
                <input
                  type="email"
                  value={formEmail.email}
                  onChange={e => setFormEmail(f => ({ ...f, email: e.target.value }))}
                  placeholder="cliente@email.com"
                  disabled={emailSending}
                />
              </div>
              <div className="form-group">
                <label>Mensaje (opcional)</label>
                <textarea
                  value={formEmail.mensaje}
                  onChange={e => setFormEmail(f => ({ ...f, mensaje: e.target.value }))}
                  placeholder="Texto que aparecerá al inicio del email..."
                  rows={3}
                  disabled={emailSending}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEmailModal(false)} disabled={emailSending}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={handleEnviarEmailSubmit} disabled={emailSending}>
                  {emailSending ? 'Enviando...' : '📧 Enviar email'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CitaForm;

